'use client';

import NextLink from 'next/link';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { Button, ImageClient, Link, Typography } from '@/components/core';
import { parseStoryblokUrl } from '@/utils/storyblok';
import styles from './PaginatedLocationGrid.module.scss';

interface PaginatedLocationGridProps {
    locations: any[]; // Adjust type if needed
    layout: string;
    link: any;
}

const ITEMS_PER_LOAD = 3;

const PaginatedLocationGrid: React.FC<PaginatedLocationGridProps> = ({ locations, layout, link }) => {
    const t = useTranslations('Common');
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);

    const handleLoadMore = () => {
        setVisibleCount(prevCount => prevCount + ITEMS_PER_LOAD);
    };

    // Only show the number of items specified by visibleCount
    const currentItems = locations.slice(0, visibleCount);
    const hasMoreItems = visibleCount < locations.length;
    return (
        <div className={styles.paginatedGrid}>
            <div className={styles.grid}>
                {currentItems.map(obj => (
                    <div
                        key={obj.uuid}
                        className={styles.cardWrapper}
                    >
                        <NextLink href={parseStoryblokUrl(`/${obj.content.page.cached_url}`)}>
                            <div className={styles.card}>
                                {obj?.content.cover?.filename && layout !== 'onlyText' && (
                                    <div className={styles.imageWrapper}>
                                        <ImageClient
                                            src={obj?.content.cover?.filename}
                                            alt={obj?.content.cover?.alt}
                                            focalPoints={obj?.content?.cover?.focus}
                                            width={500}
                                            height={500}
                                            className={styles.image}
                                        />
                                        {obj.content.name && (
                                            <>
                                                <div className={styles.layer} />
                                                <Typography
                                                    className={styles.cardTitle}
                                                    as="h3"
                                                >
                                                    {obj.content.name.replace(/\([^)]*\)/g, '')}
                                                </Typography>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </NextLink>
                    </div>
                ))}
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    margin: '20px 0 36px',
                }}
            >
                {hasMoreItems && (
                    <div className={styles.loadMore}>
                        <Button onClick={handleLoadMore}>{t('loadMore')}</Button>
                    </div>
                )}

                {link?.[0]?.link && (
                    <div className={styles.action}>
                        <Link
                            href={
                                link?.[0].link.url
                                    ? parseStoryblokUrl(`/${link?.[0].link.url}`)
                                    : parseStoryblokUrl(`/${link?.[0].link.cached_url}`)
                            }
                            target={link?.[0]?.link?.target}
                        >
                            <Button>{link?.[0].title}</Button>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaginatedLocationGrid;
